import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { XMarkIcon, DevicePhoneMobileIcon, ComputerDesktopIcon } from '@heroicons/react/24/outline';

const InstallPrompt = () => {
  const { t } = useTranslation();
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isSafari, setIsSafari] = useState(false);

  useEffect(() => {
    // Check if app is already installed
    const checkStandalone = () => {
      return (
        window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone === true
      );
    };

    // Check if device is iOS
    const checkIOS = () => {
      return (
        /iPad|iPhone|iPod/.test(navigator.userAgent) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
      );
    };

    // Check if browser is Safari
    const checkSafari = () => {
      const ua = navigator.userAgent.toLowerCase();
      return ua.includes('safari') && !ua.includes('chrome') && !ua.includes('crios') && !ua.includes('fxios');
    };

    setIsIOS(checkIOS());
    setIsSafari(checkSafari());
    setIsStandalone(checkStandalone());

    // EV-845 — NEVER nag on a SHARED DEEP-LINK. iOS Safari can't tell whether the
    // PWA is already installed, so a recipient who opens a shared event/offer/menu
    // link (and likely already has the app) was getting the install prompt. A
    // shared-link visitor should just SEE the content. Detect those routes/params.
    const isSharedDeepLink = () => {
      try {
        const p = window.location.pathname || '';
        const s = window.location.search || '';
        return /^\/(event|events|at|menu|offer|offers|activity|activities|redeem|redeem-drinks|wifi|share)\b/i.test(p)
          || /[?&](ref|lang|m|via|share)=/i.test(s);
      } catch { return false; }
    };

    // Patrick rule 2026-04-26: re-prompt until installed — but EV-845 (2026-06-23):
    // it was too naggy. Fixes: (a) the "Got it" dismiss was writing a DEAD key so it
    // never suppressed → fixed in handleDismiss; (b) re-nag window 24h → 7 days; (c)
    // never auto-show on a shared deep-link.
    const lastDismissed = parseInt(localStorage.getItem('pwa-install-prompt-dismissed-at') || '0', 10);
    const dismissedRecently = lastDismissed && (Date.now() - lastDismissed) < 30 * 24 * 60 * 60 * 1000;

    if (!checkStandalone() && !dismissedRecently && !isSharedDeepLink()) {
      // For iOS, show prompt after 3 seconds (Safari OR other iOS browser)
      if (checkIOS()) {
        setTimeout(() => setShowPrompt(true), 3000);
      }
    }

    // Listen for beforeinstallprompt (Android/Windows)
    // Chrome fires this only when site meets installability criteria:
    // SW registered + valid manifest + HTTPS + user engagement (~30s).
    // Once user dismisses/installs, Chrome won't refire for ~3 months.
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // EV-845 — respect the 7-day dismiss window + never nag on a shared deep-link.
      const last = parseInt(localStorage.getItem('pwa-install-prompt-dismissed-at') || '0', 10);
      const recentlyDismissed = last && (Date.now() - last) < 30 * 24 * 60 * 60 * 1000;
      if (!recentlyDismissed && !isSharedDeepLink()) setShowPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      // Android/Windows install
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log('PWA install prompt outcome:', outcome);
      setDeferredPrompt(null);
      setShowPrompt(false);
      localStorage.setItem('pwa-install-prompt-dismissed-at', String(Date.now()));
    } else if (isIOS) {
      // For iOS, just mark as shown (user will follow manual instructions)
      setShowPrompt(false);
      localStorage.setItem('pwa-install-prompt-dismissed-at', String(Date.now()));
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    // EV-845 — FIX: write the key the suppression check actually reads. The old
    // 'pwa-install-prompt-shown' key was never read, so "Got it!" never suppressed
    // the prompt and users got nagged on every visit.
    localStorage.setItem('pwa-install-prompt-dismissed-at', String(Date.now()));
  };

  if (isStandalone || !showPrompt) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 100 }}
        style={{
          position: 'fixed',
          bottom: '20px',
          left: '20px',
          right: '20px',
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '20px',
          boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
          zIndex: 10000,
          border: '1px solid #e5e7eb',
          maxWidth: '500px',
          margin: '0 auto'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          <div
            style={{
              backgroundColor: '#184A99',
              borderRadius: '12px',
              padding: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {isIOS ? (
              <DevicePhoneMobileIcon style={{ height: '24px', width: '24px', color: 'white' }} />
            ) : (
              <ComputerDesktopIcon style={{ height: '24px', width: '24px', color: 'white' }} />
            )}
          </div>

          <div style={{ flex: 1 }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '600', color: '#111827' }}>
              {t('install.title', 'Install HonestLee')}
            </h3>

            {isIOS ? (
              isSafari ? (
                <div>
                  <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#6b7280', lineHeight: '1.4' }}>
                    {t('install.iosLead', 'Add to your home screen for quick access!')}
                  </p>
                  <div
                    style={{
                      backgroundColor: '#f3f4f6',
                      borderRadius: '8px',
                      padding: '12px',
                      fontSize: '13px',
                      color: '#374151'
                    }}
                  >
                    <strong>{t('install.howTo', 'How to install:')}</strong>
                    <br />
                    1. {t('install.step1', 'Tap the Share button')} <span style={{ fontSize: '16px' }}>⎙</span>
                    <br />
                    2. {t('install.step2', 'Scroll and tap "Add to Home Screen"')}
                    <br />
                    3. {t('install.step3', 'Tap "Add" to confirm')}
                  </div>
                </div>
              ) : (
                <div>
                  <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#6b7280', lineHeight: '1.4' }}>
                    {t('install.safariLead', 'To install, please open this website in Safari')}
                  </p>
                  <div
                    style={{
                      backgroundColor: '#fef3c7',
                      borderRadius: '8px',
                      padding: '12px',
                      fontSize: '13px',
                      color: '#92400e',
                      border: '1px solid #fde68a'
                    }}
                  >
                    <strong>⚠️ {t('install.noteLabel', 'Note:')}</strong> {t('install.safariNote', 'Installation is only available in Safari browser on iOS')}
                  </div>
                </div>
              )
            ) : (
              <div>
                <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#6b7280', lineHeight: '1.4' }}>
                  {t('install.androidLead', 'Install this app for a better experience!')}
                </p>
              </div>
            )}

            <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
              {!isIOS && deferredPrompt ? (
                <button
                  onClick={handleInstall}
                  style={{
                    backgroundColor: '#184A99',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    flex: 1
                  }}
                >
                  {t('install.installBtn', 'Install App')}
                </button>
              ) : null}
              <button
                onClick={handleDismiss}
                style={{
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  flex: isIOS ? 1 : 'none'
                }}
              >
                {t('install.gotIt', 'Got it!')}
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default InstallPrompt;
